// Helper around the /wizard-drafts backend module.
//
// Drafts are keyed by (user, kind, entity_id). Pre-create wizards (the entity
// doesn't exist yet) pass entityId = null. Once the underlying record is
// created the wizard calls attachEntity(kind, newId) so subsequent saves
// land on the entity-scoped row.
//
// Save calls are debounced per (kind, entityId) so rapid field edits collapse
// into a single PUT. Callers can await flushDraft() to force the pending
// request (used right before navigating away or completing the wizard).

import { api } from './api.js';

const DEFAULT_DEBOUNCE_MS = 400;
const pending = new Map(); // key -> { timer, lastPayload, resolvers, inflight }

function keyFor(kind, entityId) {
  return `${kind}::${entityId || ''}`;
}

function entityQuery(entityId) {
  return entityId ? `?entity_id=${encodeURIComponent(entityId)}` : '';
}

export async function loadDraft(kind, entityId = null) {
  const res = await api.get(`/wizard-drafts/${encodeURIComponent(kind)}${entityQuery(entityId)}`);
  // 204 → api returns null; otherwise { data: {...} } or { data: null }
  if (!res || !res.data) return null;
  return res.data;
}

/** Immediate (non-debounced) save — use on step transitions / before submit. */
export async function saveDraftNow(kind, { entityId = null, step = '', payload = {} } = {}) {
  const res = await api.put(`/wizard-drafts/${encodeURIComponent(kind)}`, {
    entity_id: entityId,
    step,
    payload,
  });
  return res?.data || null;
}

/**
 * Debounced save. Coalesces rapid changes; the last call's payload wins.
 * Returns a promise that resolves with the server row once the debounced
 * request lands.
 */
export function saveDraftDebounced(kind, { entityId = null, step = '', payload = {} } = {}, ms = DEFAULT_DEBOUNCE_MS) {
  const key = keyFor(kind, entityId);
  let slot = pending.get(key);
  if (!slot) {
    slot = { timer: null, lastPayload: null, resolvers: [], inflight: null };
    pending.set(key, slot);
  }
  slot.lastPayload = { entityId, step, payload };
  if (slot.timer) clearTimeout(slot.timer);
  return new Promise((resolve, reject) => {
    slot.resolvers.push({ resolve, reject });
    slot.timer = setTimeout(async () => {
      slot.timer = null;
      const args = slot.lastPayload;
      const resolvers = slot.resolvers;
      slot.resolvers = [];
      try {
        slot.inflight = saveDraftNow(kind, args);
        const row = await slot.inflight;
        resolvers.forEach((r) => r.resolve(row));
      } catch (err) {
        resolvers.forEach((r) => r.reject(err));
      } finally {
        slot.inflight = null;
      }
    }, ms);
  });
}

/** Wait for any pending debounced save for (kind, entityId) to finish. */
export async function flushDraft(kind, entityId = null) {
  const slot = pending.get(keyFor(kind, entityId));
  if (!slot) return;
  if (slot.timer) {
    // Fire immediately instead of waiting out the debounce window.
    clearTimeout(slot.timer);
    slot.timer = null;
    const args = slot.lastPayload;
    const resolvers = slot.resolvers;
    slot.resolvers = [];
    try {
      slot.inflight = saveDraftNow(kind, args);
      const row = await slot.inflight;
      resolvers.forEach((r) => r.resolve(row));
    } catch (err) {
      resolvers.forEach((r) => r.reject(err));
      throw err;
    } finally {
      slot.inflight = null;
    }
    return;
  }
  if (slot.inflight) await slot.inflight.catch(() => {});
}

export async function attachDraftEntity(kind, entityId) {
  const res = await api.post(
    `/wizard-drafts/${encodeURIComponent(kind)}/attach-entity`,
    { entity_id: entityId },
  );
  return res?.data || null;
}

export async function completeDraft(kind, entityId = null) {
  await flushDraft(kind, entityId);
  return api.post(`/wizard-drafts/${encodeURIComponent(kind)}/complete${entityQuery(entityId)}`, {});
}

export const WIZARD_KIND = Object.freeze({
  PROPERTY_CREATE: 'property_create',
});
